import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, AlertTriangle, CalendarClock, User as UserIcon } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/assignments";

export const ACTIVITY_QUICK = ["Preparare preventivo", "Inviare preventivo", "Richiamare cliente", "Verificare accettazione preventivo", "Nuovo sopralluogo", "Richiedere documentazione", "Altro"];
export const ACT_STATUS = ["Da fare", "In corso", "Completata", "Annullata"];
export const ACT_PRIORITY = [["Media", "Normale"], ["Alta", "Alta"], ["Urgente", "Urgente"]];
const prLabel = (p) => (ACT_PRIORITY.find(([v]) => v === p) || [p, p])[1];
const prTone = (p) => p === "Urgente" ? "bg-danger/10 text-danger" : p === "Alta" ? "bg-warning/15 text-warning" : "bg-slate-100 text-slate-600";
const stTone = (s) => s === "Completata" ? "bg-success/10 text-success" : s === "Annullata" ? "bg-slate-100 text-slate-400 line-through" : s === "In corso" ? "bg-brand/10 text-brand" : "bg-slate-100 text-slate-700";

export default function ActivitiesTab({ assignment, canManage, onChanged }) {
  const [rows, setRows] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const id = assignment.id;

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/assignments/${id}/activities`); setRows(data); }
    catch { setRows([]); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (a, body) => {
    try {
      await api.patch(`/assignments/${id}/activities/${a.id}`, body);
      toast.success("Attività aggiornata");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };

  const open = (rows || []).filter((a) => ["Da fare", "In corso"].includes(a.status));
  const closed = (rows || []).filter((a) => !["Da fare", "In corso"].includes(a.status));
  const overdue = open.filter((a) => a.is_overdue).length;

  return (
    <div className="space-y-3" data-testid="activities-tab">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-slate-500" data-testid="activities-summary">
          {open.length} aperte{overdue ? <span className="ml-1 rounded bg-danger px-1.5 py-0.5 text-[10px] font-semibold text-white" data-testid="activities-overdue-count">{overdue} scadute</span> : null}
        </div>
        {canManage && (
          <Button size="sm" className="gap-1.5" onClick={() => setShowNew(true)} data-testid="activity-new">
            <Plus className="h-3.5 w-3.5" /> Nuova attività / Promemoria
          </Button>
        )}
      </div>
      {rows === null ? (
        <div className="py-6 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-brand" /></div>
      ) : open.length === 0 && closed.length === 0 ? (
        <p className="py-4 text-center text-xs text-slate-400" data-testid="activities-empty">Nessuna attività programmata.</p>
      ) : (
        <>
          <ul className="space-y-2" data-testid="activities-open">
            {open.map((a) => <ActivityRow key={a.id} a={a} canManage={canManage} onPatch={patch} />)}
          </ul>
          {closed.length > 0 && (
            <div>
              <button className="text-xs text-brand hover:underline" onClick={() => setShowClosed((v) => !v)} data-testid="activities-toggle-closed">
                {showClosed ? "Nascondi" : "Mostra"} {closed.length} completate/annullate
              </button>
              {showClosed && <ul className="mt-2 space-y-2 opacity-80" data-testid="activities-closed">{closed.map((a) => <ActivityRow key={a.id} a={a} canManage={canManage} onPatch={patch} />)}</ul>}
            </div>
          )}
        </>
      )}
      {showNew && <NewActivityDialog assignment={assignment} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); onChanged?.(); }} />}
    </div>
  );
}

function ActivityRow({ a, canManage, onPatch }) {
  const isOpen = ["Da fare", "In corso"].includes(a.status);
  return (
    <li className={`rounded-md border px-3 py-2 ${a.is_overdue ? "border-danger/40 bg-danger/5" : a.is_due_today ? "border-warning/40 bg-warning/5" : "border-slate-100"}`} data-testid={`activity-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${stTone(a.status)}`} data-testid={`activity-status-${a.id}`}>{a.status}</span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${prTone(a.priority)}`}>{prLabel(a.priority)}</span>
            {a.is_overdue && <span className="flex items-center gap-0.5 text-[10px] font-semibold text-danger" data-testid={`activity-overdue-${a.id}`}><AlertTriangle className="h-3 w-3" /> SCADUTA</span>}
            {a.is_due_today && !a.is_overdue && <span className="text-[10px] font-semibold text-warning">Scade oggi</span>}
          </div>
          <p className="mt-1 text-sm font-medium text-slate-800">{a.description}</p>
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {fmtDate(a.activity_date)}{a.activity_time ? ` ${a.activity_time}` : ""}</span>
            <span className="flex items-center gap-1"><UserIcon className="h-3 w-3" /> {a.operator_name || "—"}</span>
            {a.assigned_by_name && <span>da {a.assigned_by_name} · {fmtDateTime(a.created_at)}</span>}
          </div>
          {a.notes && <p className="mt-1 whitespace-pre-wrap text-xs text-slate-600">{a.notes}</p>}
        </div>
        {canManage && isOpen && (
          <Select value={a.status} onValueChange={(v) => onPatch(a, { status: v })}>
            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`activity-status-select-${a.id}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{ACT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
    </li>
  );
}

function NewActivityDialog({ assignment, onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ type: "Altro", description: "", operator_id: "", activity_date: "", activity_time: "", priority: "Media", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { api.get("/users/assignable").then(({ data }) => setUsers(data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!form.description.trim()) { toast.error("Indica la descrizione"); return; }
    if (!form.activity_date) { toast.error("Indica la data di scadenza"); return; }
    setBusy(true);
    try {
      await api.post(`/assignments/${assignment.id}/activities`, {
        type: form.type, description: form.description.trim(), operator_id: form.operator_id || undefined,
        activity_date: form.activity_date, activity_time: form.activity_time || undefined, priority: form.priority, notes: form.notes || undefined,
      });
      toast.success("Attività creata");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-md" data-testid="activity-dialog">
        <DialogHeader><DialogTitle>Nuova attività / Promemoria</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Tipo</label>
            <Select value={form.type} onValueChange={(v) => { set("type", v); if (v !== "Altro" && !form.description) set("description", v); }}>
              <SelectTrigger data-testid="activity-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{ACTIVITY_QUICK.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Descrizione *</label>
            <Input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Es. Preparare preventivo fotovoltaico 6 kW con accumulo" data-testid="activity-description" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Incaricato</label>
            <Select value={form.operator_id || "me"} onValueChange={(v) => set("operator_id", v === "me" ? "" : v)}>
              <SelectTrigger data-testid="activity-assignee"><SelectValue placeholder="Io" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="me">Io stesso/a</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}{u.role === "admin" ? " · Direzione" : u.role === "backoffice" ? " · Back Office" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Scadenza *</label>
              <Input type="date" value={form.activity_date} onChange={(e) => set("activity_date", e.target.value)} data-testid="activity-date" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Ora</label>
              <Input type="time" value={form.activity_time} onChange={(e) => set("activity_time", e.target.value)} data-testid="activity-time" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Priorità</label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="activity-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{ACT_PRIORITY.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Note aggiuntive (opz.)" rows={2} data-testid="activity-notes" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="activity-submit">{busy ? "Salvataggio…" : "Crea attività"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
